import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Checkbox } from '@openx/design-system/components/Checkbox/Checkbox.tsx';
import { TableBody } from '@openx/design-system/components/Table/TableBody.tsx';
import { TableCell } from '@openx/design-system/components/Table/TableCell.tsx';
import { TableHead } from '@openx/design-system/components/Table/TableHead.tsx';
import { TableRow } from '@openx/design-system/components/Table/TableRow.tsx';
import { TableStickyCell } from '@openx/design-system/components/Table/TableStickyCell.tsx';
import { useTranslation } from 'react-i18next';
import { OpenXAgentSelectAllCheckbox } from '../../shared/OpenXAgentSelectAllCheckbox.tsx';
import { OpenXAgentTable, OpenXAgentTableEmptyRow } from '../../shared/OpenXAgentTable.tsx';
import { OpenXAgentTableSortLabel } from '../../shared/OpenXAgentTableSortLabel.tsx';
import { CHECKBOX_COLUMN_WIDTH } from '../../shared/utils/consts.ts';
import { usePagination } from '../../shared/utils/usePagination.ts';
import { getSortDirection, useSorting } from '../../shared/utils/useSorting.ts';
import { SORT_ACCESSORS, TABLE_COLUMNS, TABLE_MIN_WIDTH } from '../utils/consts.ts';
import type { OpenXAgentMatchedSegment } from '../utils/types.ts';

type MatchedSegmentTableProps = {
  items: OpenXAgentMatchedSegment[];
  selectedIds: Set<string>;
  onToggleItem: (id: string) => void;
  onSelectAll: () => void;
  onSelectFree: () => void;
  onDeselectAll: () => void;
};

export const MatchedSegmentTable = ({
  items,
  selectedIds,
  onToggleItem,
  onSelectAll,
  onSelectFree,
  onDeselectAll,
}: MatchedSegmentTableProps) => {
  const { t } = useTranslation();
  const { sortedItems, sorting } = useSorting(items, SORT_ACCESSORS);
  const { pageItems, pagination } = usePagination(sortedItems);

  return (
    <OpenXAgentTable
      ariaLabel={t('Matched segments')}
      dataTest="openx-agent-matched-segment-table"
      columns={TABLE_COLUMNS}
      minWidth={TABLE_MIN_WIDTH}
      pagination={pagination}
    >
      <TableHead>
        <TableRow>
          <TableStickyCell sticky="left" header padding="checkbox">
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <OpenXAgentSelectAllCheckbox
                itemCount={items.length}
                selectedCount={items.filter(({ id }) => selectedIds.has(id)).length}
                ariaLabel={t('Select all segments')}
                onSelectAll={onSelectAll}
                onDeselectAll={onDeselectAll}
                menuItems={[
                  { label: t('All Segments'), onClick: onSelectAll },
                  { label: t('Free Segments'), onClick: onSelectFree },
                  { label: t('None'), onClick: onDeselectAll },
                ]}
              />
            </Box>
          </TableStickyCell>

          <TableStickyCell
            sticky="left"
            header
            edge
            offset={CHECKBOX_COLUMN_WIDTH}
            sortDirection={getSortDirection(sorting, 'name')}
          >
            <OpenXAgentTableSortLabel sorting={sorting} columnId="name">
              {t('Segment Name')}
            </OpenXAgentTableSortLabel>
          </TableStickyCell>

          <TableCell sortDirection={getSortDirection(sorting, 'description')}>
            <OpenXAgentTableSortLabel sorting={sorting} columnId="description">
              {t('Description')}
            </OpenXAgentTableSortLabel>
          </TableCell>

          <TableCell sortDirection={getSortDirection(sorting, 'category')}>
            <OpenXAgentTableSortLabel sorting={sorting} columnId="category">
              {t('Category')}
            </OpenXAgentTableSortLabel>
          </TableCell>

          <TableCell sortDirection={getSortDirection(sorting, 'dataPartner')}>
            <OpenXAgentTableSortLabel sorting={sorting} columnId="dataPartner">
              {t('Data Partner')}
            </OpenXAgentTableSortLabel>
          </TableCell>

          <TableCell sortDirection={getSortDirection(sorting, 'price')}>
            <OpenXAgentTableSortLabel sorting={sorting} columnId="price">
              {t('Price')}
            </OpenXAgentTableSortLabel>
          </TableCell>

          <TableCell sortDirection={getSortDirection(sorting, 'uniqueUsers')}>
            <OpenXAgentTableSortLabel sorting={sorting} columnId="uniqueUsers">
              {t('Unique Users')}
            </OpenXAgentTableSortLabel>
          </TableCell>

          <TableCell sortDirection={getSortDirection(sorting, 'ctvDevices')}>
            <OpenXAgentTableSortLabel sorting={sorting} columnId="ctvDevices">
              {t('CTV Devices')}
            </OpenXAgentTableSortLabel>
          </TableCell>

          <TableCell sortDirection={getSortDirection(sorting, 'activeIps')}>
            <OpenXAgentTableSortLabel sorting={sorting} columnId="activeIps">
              {t('Active IPs')}
            </OpenXAgentTableSortLabel>
          </TableCell>

          <TableCell sortDirection={getSortDirection(sorting, 'mobileDevices')}>
            <OpenXAgentTableSortLabel sorting={sorting} columnId="mobileDevices">
              {t('Mobile Devices')}
            </OpenXAgentTableSortLabel>
          </TableCell>
        </TableRow>
      </TableHead>

      <TableBody>
        {pageItems.map(
          ({
            id,
            name,
            description,
            category,
            dataPartner,
            price,
            uniqueUsers,
            ctvDevices,
            activeIps,
            mobileDevices,
          }) => (
            <TableRow hover key={id} data-test={`openx-agent-matched-segment-row-${id}`}>
              <TableStickyCell sticky="left" padding="checkbox">
                <Checkbox
                  checked={selectedIds.has(id)}
                  onChange={() => onToggleItem(id)}
                  slotProps={{ input: { 'aria-label': t('Select {name}', { name }) } }}
                />
              </TableStickyCell>

              <TableStickyCell
                sticky="left"
                edge
                offset={CHECKBOX_COLUMN_WIDTH}
                sx={{ fontWeight: 'fontWeightBold', overflowWrap: 'anywhere' }}
              >
                {name}
              </TableStickyCell>

              <TableCell>
                <Typography noWrap sx={{ font: 'inherit' }}>
                  {description}
                </Typography>
              </TableCell>

              <TableCell>{category}</TableCell>
              <TableCell>{dataPartner}</TableCell>
              <TableCell>{[price.percentOfMedia, price.fixedCpm].filter(Boolean).join(' / ')}</TableCell>
              <TableCell>{uniqueUsers}</TableCell>
              <TableCell>{ctvDevices}</TableCell>
              <TableCell>{activeIps}</TableCell>
              <TableCell>{mobileDevices}</TableCell>
            </TableRow>
          )
        )}

        {items.length === 0 ? (
          <OpenXAgentTableEmptyRow colSpan={TABLE_COLUMNS.length}>{t('No segments found')}</OpenXAgentTableEmptyRow>
        ) : null}
      </TableBody>
    </OpenXAgentTable>
  );
};
